import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { useCookies } from "react-cookie";

// bootstrap imports
import Container from "react-bootstrap/Container";
import { Button, Badge, Form } from "react-bootstrap";
import { Pagination } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";

// app-specific
import Table from "../components/Table";
import NavBar from "../components/NavBar";
import { NavigateRoutes } from "../constants/routes";

import {
  IoInformationCircleOutline,
  IoVideocam,
  IoTrash,
  IoTime,
  IoCloseCircle,
  IoPersonRemove,
} from "react-icons/io5";

// utils
import { tokenIsValid, decode, getTokenExpirationDate } from "../utils/token";

import {
  getAllInterviews,
  deleteInterview,
  cancelInterview,
  rescheduleInterview,
  markInterviewNoShow,
} from "../api/Interviews.js";

// custom page styling
import styles from "../styles/Admin.module.css";

const PAGE_SIZE = 10;
const BATCH_SIZE = 50;
const REFRESH_INTERVAL_MS = 300000; // 5 minutes, matching the trainings page

// fixed so every choice is always offered, whether or not any interview
// currently uses it; "Both" is the default and applies no filter
const BOTH_LANGUAGES = "Both";
const LANGUAGE_OPTIONS = ["English", "Spanish", BOTH_LANGUAGES];

// records created before cancelling existed have no status and count as scheduled
const STATUS_CANCELLED = "cancelled";
const STATUS_NO_SHOW = "no_show";
const isCancelled = (record) => record.status === STATUS_CANCELLED;
const isNoShow = (record) => record.status === STATUS_NO_SHOW;
// an interview that is not going ahead as it stands
const isSettled = (record) => isCancelled(record) || isNoShow(record);

// how many earlier dates for this interview the candidate missed, from its history
const missedBefore = (record) =>
  (record.reschedule_history || []).filter(
    (entry) => entry.previous_status === STATUS_NO_SHOW,
  ).length;

// Interview times are entered and shown in Central time, but the browser reads
// them as its own local time. That is exact for anyone working in Central time
// and off by the time zone difference for anyone who is not.
const interviewStart = (dateIso, timeIso) => {
  if (!dateIso || !timeIso) return null;
  const start = new Date(`${dateIso}T${timeIso}`);
  return Number.isNaN(start.getTime()) ? null : start;
};

const isPast = (record) => {
  const start = interviewStart(
    record.interview_date_iso,
    record.interview_time_iso,
  );
  return start !== null && start < new Date();
};

const RESCHEDULE_UNCHANGED = "Change the date or time to reschedule.";

/**
 * Page component for displaying scheduled RA interviews.
 * Mirrors the layout of ExistingTrainingPage, minus the actions that do not
 * apply to a one-candidate/one-interviewer interview.
 * @returns React.FC
 */
const ExistingInterviewsPage = () => {
  // state - the page of interviews currently displayed
  const [interviews, setInterviews] = useState([]);
  const [curPage, setCurPage] = useState(1);
  const [totalPage, setTotalPage] = useState();
  const [selectedLanguage, setSelectedLanguage] = useState(BOTH_LANGUAGES);

  // client-side cache with lazy loading
  const [interviewCache, setInterviewCache] = useState([]);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [isRefreshingCache, setIsRefreshingCache] = useState(false);
  const [hasMoreInterviews, setHasMoreInterviews] = useState(false);
  const [interviewsOffset, setInterviewsOffset] = useState(0);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [loadError, setLoadError] = useState("");

  // state - get user info
  const [userData, setUserData] = useState();

  // state - whether the user is able to access this page
  const [authorized, setAuthorized] = useState();
  const [reauthorized, setReauthorized] = useState(false);

  // info modal
  const [modalShow, setModalShow] = useState(false);
  const [modalHeader, setModalHeader] = useState("");
  const [modalBody, setModalBody] = useState("");

  // delete confirmation
  const [deleteConfirmShow, setDeleteConfirmShow] = useState(false);
  const [deleteInterviewData, setDeleteInterviewData] = useState(null);
  const [isDeletingInterview, setIsDeletingInterview] = useState(false);

  // cancel confirmation
  const [cancelConfirmShow, setCancelConfirmShow] = useState(false);
  const [cancelInterviewData, setCancelInterviewData] = useState(null);
  const [isCancellingInterview, setIsCancellingInterview] = useState(false);
  const [cancelError, setCancelError] = useState("");

  // no show confirmation
  const [noShowConfirmShow, setNoShowConfirmShow] = useState(false);
  const [noShowInterviewData, setNoShowInterviewData] = useState(null);
  const [isMarkingNoShow, setIsMarkingNoShow] = useState(false);
  const [noShowError, setNoShowError] = useState("");

  // reschedule popup
  const [rescheduleShow, setRescheduleShow] = useState(false);
  const [rescheduleInterviewData, setRescheduleInterviewData] = useState(null);
  const [newInterviewDate, setNewInterviewDate] = useState("");
  const [newInterviewTime, setNewInterviewTime] = useState("");
  const [isReschedulingInterview, setIsReschedulingInterview] = useState(false);
  const [rescheduleError, setRescheduleError] = useState("");

  // navigate hook
  const navigate = useNavigate();

  // cookies hook
  const [cookies] = useCookies(["token", "firebaseToken"]);

  const redirectLogin = useCallback(
    () => navigate(NavigateRoutes.LOGIN + "?redirect=EXISTINGINTERVIEWS"),
    [navigate],
  );

  // Initial authorization check
  useEffect(() => {
    document.title = "Project DUAL Apps: Existing RA Interviews";
    const token = cookies["token"];

    if (token && tokenIsValid(token)) {
      const decoded = decode(token);

      if (decoded) {
        setUserData(decoded);
        setAuthorized(true);
        setReauthorized(true);
      }
    } else {
      setAuthorized(false);
    }
  }, [cookies]);

  // =================================
  // -- Data loading --
  // =================================

  const fetchAndCacheAllInterviews = useCallback(() => {
    setIsRefreshingCache(true);
    setLoadError("");

    return getAllInterviews(cookies["token"], BATCH_SIZE, 0)
      .then((res) => {
        if (res.data.success) {
          setInterviewCache(res.data.data);
          setHasMoreInterviews(res.data.has_more);
          setInterviewsOffset(BATCH_SIZE);
          setLastRefreshTime(new Date());
        } else {
          setLoadError("The server could not return the interview list.");
        }
        setIsRefreshingCache(false);
      })
      .catch((err) => {
        console.error("Error fetching interviews:", err);
        setLoadError(
          err.response?.data?.detail ||
            "Could not load interviews. Please refresh and try again.",
        );
        setIsRefreshingCache(false);
      });
  }, [cookies]);

  const loadMoreInterviews = useCallback(() => {
    if (isFetchingMore || !hasMoreInterviews) return;

    setIsFetchingMore(true);
    getAllInterviews(cookies["token"], BATCH_SIZE, interviewsOffset)
      .then((res) => {
        if (res.data.success) {
          setInterviewCache((prevCache) => [...prevCache, ...res.data.data]);
          setHasMoreInterviews(res.data.has_more);
          setInterviewsOffset(interviewsOffset + BATCH_SIZE);
        }
        setIsFetchingMore(false);
      })
      .catch((err) => {
        console.error("Error loading more interviews:", err);
        setIsFetchingMore(false);
      });
  }, [cookies, interviewsOffset, hasMoreInterviews, isFetchingMore]);

  // Fetch once the user is authorized
  useEffect(() => {
    if (reauthorized) {
      fetchAndCacheAllInterviews();
    }
  }, [reauthorized, fetchAndCacheAllInterviews]);

  // Filter the cache by the selected language
  const getFilteredInterviews = useCallback(() => {
    if (selectedLanguage === BOTH_LANGUAGES) {
      return interviewCache;
    }
    return interviewCache.filter(
      (record) => record.ra_language === selectedLanguage,
    );
  }, [interviewCache, selectedLanguage]);

  const getPaginatedFilteredData = useCallback(
    (page, pageSize) => {
      const filteredData = getFilteredInterviews();
      if (filteredData.length === 0) return { data: [], totalPage: 0 };

      const totalPages = Math.ceil(filteredData.length / pageSize);

      if (page > totalPages) {
        return { data: [], totalPage: totalPages };
      }

      const startIdx = (page - 1) * pageSize;

      return {
        data: filteredData.slice(startIdx, startIdx + pageSize),
        totalPage: totalPages,
      };
    },
    [getFilteredInterviews],
  );

  // Update the displayed page whenever the cache, page, or filters change
  useEffect(() => {
    if (reauthorized) {
      const paginatedResult = getPaginatedFilteredData(curPage, PAGE_SIZE);
      setInterviews(paginatedResult.data);
      setTotalPage(paginatedResult.totalPage);
    }
  }, [reauthorized, interviewCache, curPage, getPaginatedFilteredData]);

  // Auto-refresh the cache on a timer, as the trainings page does
  useEffect(() => {
    if (reauthorized) {
      const timer = setInterval(
        () => fetchAndCacheAllInterviews(),
        REFRESH_INTERVAL_MS,
      );
      return () => clearInterval(timer);
    }
  }, [reauthorized, fetchAndCacheAllInterviews]);

  // Token expiry
  useEffect(() => {
    const exp = getTokenExpirationDate(cookies["token"]);
    if (exp) {
      const now = new Date();
      const newTimeout = setTimeout(
        () => setAuthorized(false),
        exp - now - 10 * 1000,
      );
      return () => clearTimeout(newTimeout);
    }
  }, [cookies]);

  // Redirect to login when unauthorized
  useEffect(() => {
    if (authorized === false) {
      const timeout = setTimeout(() => redirectLogin(), 5000);
      return () => clearTimeout(timeout);
    }
  }, [authorized, redirectLogin]);

  // =================================
  // -- Button handlers --
  // =================================

  const infoInterview = (record) => {
    setModalHeader("Interview Details");
    setModalBody(
      <Container>
        <p>
          <b>Candidate</b>
          <br />
          {record.candidate_full_name}
          <br />
          {record.candidate_email}
          <br />
          {record.candidate_phone}
        </p>
        <hr />
        <p>
          <b>Interviewer</b>
          <br />
          {record.interviewer_full_name}
          <br />
          {record.interviewer_email}
          <br />
          {record.interviewer_phone}
        </p>
        <hr />
        <p>
          <b>Interview</b>
          <br />
          {record.interview_date} at {record.interview_time}
          <br />
          RA language: {record.ra_language}
        </p>
        <hr />
        <p>
          <b>Zoom</b>
          <br />
          Meeting ID: {record.interviewer_zoom_id}
          <br />
          <a
            href={record.interviewer_zoom_url}
            target="_blank"
            rel="noreferrer"
          >
            {record.interviewer_zoom_url}
          </a>
        </p>
        {record.scheduler_email && (
          <>
            <hr />
            <p>
              <b>Scheduler</b>
              <br />
              {record.scheduler_email}
            </p>
          </>
        )}
        {isCancelled(record) && (
          <>
            <hr />
            <p>
              <b>This interview has been cancelled.</b>
            </p>
          </>
        )}
        {isNoShow(record) && (
          <>
            <hr />
            <p>
              <b>The candidate did not show up for this interview.</b>
              <br />
              Rescheduling this interview books a second attempt and clears the
              no show.
            </p>
          </>
        )}
        {missedBefore(record) > 0 && !isNoShow(record) && (
          <>
            <hr />
            <p>
              <b>
                Previously a no show{" "}
                {missedBefore(record) > 1 && `(${missedBefore(record)} times)`}
              </b>
              <br />
              The dates the candidate missed are marked below.
            </p>
          </>
        )}
        {(record.reschedule_history || []).length > 0 && (
          <>
            <hr />
            <p>
              <b>Previously scheduled for</b>
              {record.reschedule_history.map((entry, idx) => (
                <React.Fragment key={idx}>
                  <br />
                  {entry.previous_interview_date} at{" "}
                  {entry.previous_interview_time}
                  {entry.previous_status === STATUS_NO_SHOW && <> (no show)</>}
                </React.Fragment>
              ))}
            </p>
          </>
        )}
      </Container>,
    );
    setModalShow(true);
  };

  const joinInterview = (record) => {
    if (!record.interviewer_zoom_url) {
      setModalHeader("No Zoom Link");
      setModalBody(
        <Container>
          <p>This interview has no Zoom URL on file.</p>
        </Container>,
      );
      setModalShow(true);
      return;
    }
    window.open(record.interviewer_zoom_url, "_blank", "noreferrer");
  };

  const handleDeleteClick = (record) => {
    setDeleteInterviewData(record);
    setDeleteConfirmShow(true);
  };

  const handleConfirmDelete = () => {
    if (!deleteInterviewData) return;

    setIsDeletingInterview(true);
    deleteInterview(deleteInterviewData.id, cookies["token"])
      .then(() => {
        setIsDeletingInterview(false);
        setDeleteConfirmShow(false);
        setDeleteInterviewData(null);
        fetchAndCacheAllInterviews();
      })
      .catch((err) => {
        console.error("Error deleting interview:", err);
        setIsDeletingInterview(false);
        setDeleteConfirmShow(false);
        setModalHeader("Error");
        setModalBody(
          <Container>
            <p>
              Failed to delete interview:{" "}
              {err.response?.data?.detail || err.message || "Unknown error"}
            </p>
          </Container>,
        );
        setModalShow(true);
      });
  };

  const showResult = (header, message) => {
    setModalHeader(header);
    setModalBody(
      <Container>
        <p>{message}</p>
      </Container>,
    );
    setModalShow(true);
  };

  const handleCancelClick = (record) => {
    setCancelInterviewData(record);
    setCancelError("");
    setCancelConfirmShow(true);
  };

  const closeCancel = () => {
    // once the emails are on their way, let the request finish
    if (isCancellingInterview) return;
    setCancelConfirmShow(false);
    setCancelInterviewData(null);
    setCancelError("");
  };

  const handleConfirmCancel = () => {
    if (!cancelInterviewData || isCancellingInterview) return;
    const record = cancelInterviewData;

    setIsCancellingInterview(true);
    setCancelError("");
    cancelInterview(record.id, cookies["token"])
      .then(() => {
        setIsCancellingInterview(false);
        setCancelConfirmShow(false);
        setCancelInterviewData(null);
        showResult(
          "Interview Cancelled",
          `The interview with ${record.candidate_full_name} on ${record.interview_date} at ${record.interview_time} is cancelled. ${record.candidate_full_name} and ${record.interviewer_full_name} have been emailed.`,
        );
        fetchAndCacheAllInterviews();
      })
      .catch((err) => {
        console.error("Error cancelling interview:", err);
        setIsCancellingInterview(false);
        setCancelError(
          `Could not cancel the interview: ${
            err.response?.data?.detail || err.message || "Unknown error"
          }`,
        );
        // someone else may have changed it in the meantime
        fetchAndCacheAllInterviews();
      });
  };

  const handleNoShowClick = (record) => {
    setNoShowInterviewData(record);
    setNoShowError("");
    setNoShowConfirmShow(true);
  };

  const closeNoShow = () => {
    // once the emails are on their way, let the request finish
    if (isMarkingNoShow) return;
    setNoShowConfirmShow(false);
    setNoShowInterviewData(null);
    setNoShowError("");
  };

  const handleConfirmNoShow = () => {
    if (!noShowInterviewData || isMarkingNoShow) return;
    const record = noShowInterviewData;

    setIsMarkingNoShow(true);
    setNoShowError("");
    markInterviewNoShow(record.id, cookies["token"])
      .then(() => {
        setIsMarkingNoShow(false);
        setNoShowConfirmShow(false);
        setNoShowInterviewData(null);
        showResult(
          "Marked as No Show",
          `${record.candidate_full_name} and ${record.interviewer_full_name} have been emailed that the interview on ${record.interview_date} at ${record.interview_time} was missed.`,
        );
        fetchAndCacheAllInterviews();
      })
      .catch((err) => {
        console.error("Error marking no show:", err);
        setIsMarkingNoShow(false);
        setNoShowError(
          `Could not mark this interview as a no show: ${
            err.response?.data?.detail || err.message || "Unknown error"
          }`,
        );
        // someone else may have changed it in the meantime
        fetchAndCacheAllInterviews();
      });
  };

  const handleRescheduleClick = (record) => {
    setRescheduleInterviewData(record);
    setNewInterviewDate(record.interview_date_iso || "");
    setNewInterviewTime(record.interview_time_iso || "");
    setRescheduleError("");
    setRescheduleShow(true);
  };

  const closeReschedule = () => {
    // once the emails are on their way, let the request finish
    if (isReschedulingInterview) return;
    setRescheduleShow(false);
    setRescheduleInterviewData(null);
    setRescheduleError("");
  };

  // why the entered date and time cannot be submitted yet, or "" if they can
  const getRescheduleProblem = () => {
    if (!rescheduleInterviewData) return "";
    if (!newInterviewDate || !newInterviewTime) {
      return "Enter both a new date and a new time.";
    }
    if (
      newInterviewDate === rescheduleInterviewData.interview_date_iso &&
      newInterviewTime === rescheduleInterviewData.interview_time_iso
    ) {
      return RESCHEDULE_UNCHANGED;
    }
    const start = interviewStart(newInterviewDate, newInterviewTime);
    if (start === null) return "That date or time is not valid.";
    if (start < new Date()) return "The new date and time are in the past.";
    return "";
  };

  const handleConfirmReschedule = () => {
    if (
      !rescheduleInterviewData ||
      isReschedulingInterview ||
      getRescheduleProblem()
    ) {
      return;
    }
    const record = rescheduleInterviewData;

    setIsReschedulingInterview(true);
    setRescheduleError("");
    rescheduleInterview(
      record.id,
      newInterviewDate,
      newInterviewTime,
      cookies["token"],
    )
      .then((res) => {
        setIsReschedulingInterview(false);
        setRescheduleShow(false);
        setRescheduleInterviewData(null);
        showResult(
          res.data.was_no_show
            ? "Second Interview Booked"
            : "Interview Rescheduled",
          `The interview with ${record.candidate_full_name} moved from ${record.interview_date} at ${record.interview_time} to ${res.data.interview_date} at ${res.data.interview_time}. ${record.candidate_full_name} and ${record.interviewer_full_name} have been emailed.` +
            (res.data.was_no_show
              ? " The no show has been lifted, and the missed date is kept in Interview Details."
              : ""),
        );
        fetchAndCacheAllInterviews();
      })
      .catch((err) => {
        console.error("Error rescheduling interview:", err);
        setIsReschedulingInterview(false);
        setRescheduleError(
          `Could not reschedule the interview: ${
            err.response?.data?.detail || err.message || "Unknown error"
          }`,
        );
        // someone else may have changed it in the meantime
        fetchAndCacheAllInterviews();
      });
  };

  // =================================
  // -- Rendering helpers --
  // =================================

  const getLanguageBadge = (language) => {
    if (language === "Spanish") {
      return <Badge bg="info">{language}</Badge>;
    }
    return <Badge bg="success">{language}</Badge>;
  };

  const InterviewRow = (props) => {
    const record = props.record;
    const cancelled = isCancelled(record);
    const noShow = isNoShow(record);
    const settled = isSettled(record);
    const past = isPast(record);
    const rescheduled = (record.reschedule_history || []).length > 0;

    let cancelTitle = "Cancel this interview and email the candidate and interviewer";
    if (cancelled) cancelTitle = "This interview is already cancelled";
    else if (noShow) cancelTitle = "This interview is marked as a no show";
    else if (past) cancelTitle = "This interview is in the past, so it cannot be cancelled";

    let noShowTitle =
      "Mark the candidate as a no show and email them and the interviewer";
    if (noShow) noShowTitle = "This interview is already marked as a no show";
    else if (cancelled) noShowTitle = "This interview is cancelled";
    else if (!past) noShowTitle = "This interview has not started yet";

    return (
      <tr style={settled ? { opacity: 0.55 } : undefined}>
        <td>{getLanguageBadge(record.ra_language)}</td>
        <td>{record.candidate_full_name}</td>
        <td>{record.interviewer_full_name}</td>
        <td>
          {record.interview_date}
          {cancelled && (
            <>
              {" "}
              <Badge bg="secondary">Cancelled</Badge>
            </>
          )}
          {noShow && (
            <>
              {" "}
              <Badge bg="dark">No show</Badge>
            </>
          )}
          {!settled && rescheduled && (
            <>
              {" "}
              <Badge bg="primary">Rescheduled</Badge>
            </>
          )}
        </td>
        <td>{record.interview_time}</td>
        <td>
          <div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => infoInterview(record)}
            >
              <IoInformationCircleOutline />
            </Button>
          </div>
        </td>
        <td>
          <div
            title={
              settled
                ? "This interview is not going ahead"
                : "Open the Zoom link for this interview"
            }
          >
            <Button
              variant="primary"
              size="sm"
              disabled={settled}
              onClick={() => joinInterview(record)}
            >
              <IoVideocam />
            </Button>
          </div>
        </td>
        <td>
          <div
            title={
              cancelled
                ? "This interview is cancelled"
                : noShow
                  ? "Book the second attempt by moving this interview to a new date and time"
                  : "Move this interview to a new date and time and email the candidate and interviewer"
            }
          >
            <Button
              variant="primary"
              size="sm"
              disabled={cancelled}
              onClick={() => handleRescheduleClick(record)}
            >
              <IoTime />
            </Button>
          </div>
        </td>
        <td>
          <div title={cancelTitle}>
            <Button
              variant="warning"
              size="sm"
              disabled={settled || past}
              onClick={() => handleCancelClick(record)}
            >
              <IoCloseCircle />
            </Button>
          </div>
        </td>
        <td>
          <div title={noShowTitle}>
            <Button
              variant="dark"
              size="sm"
              disabled={settled || !past}
              onClick={() => handleNoShowClick(record)}
            >
              <IoPersonRemove />
            </Button>
          </div>
        </td>
        {userData && userData.is_admin && (
          <td>
            <div>
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleDeleteClick(record)}
                title="Permanently delete this record. Nobody is emailed."
              >
                <IoTrash />
              </Button>
            </div>
          </td>
        )}
      </tr>
    );
  };

  const handleChangePage = useCallback(
    (page) => {
      const itemsNeeded = page * PAGE_SIZE;

      if (itemsNeeded > interviewCache.length && hasMoreInterviews) {
        loadMoreInterviews();
      }

      setCurPage(page);
    },
    [interviewCache.length, hasMoreInterviews, loadMoreInterviews],
  );

  function MyPagination({ total, current, onChangePage }) {
    let items = [];

    if (current > 1) {
      items.push(
        <Pagination.Prev
          key="prev"
          onClick={() => onChangePage(current - 1)}
        />,
      );
    }

    for (let page = 1; page <= total; page++) {
      items.push(
        <Pagination.Item
          key={page}
          data-page={page}
          active={page === current}
          onClick={() => onChangePage(page)}
        >
          {page}
        </Pagination.Item>,
      );
    }

    if (current < total) {
      items.push(
        <Pagination.Next
          key="next"
          onClick={() => onChangePage(current + 1)}
        />,
      );
    }

    return <Pagination>{items}</Pagination>;
  }

  const handleLanguageSort = (info) => {
    setSelectedLanguage(info);
    setCurPage(1);
  };

  const baseColumns = [
    "Language",
    "Candidate Name",
    "Interviewer Name",
    "Day to Meet",
    "Time to Meet (in CST)",
    "Interview Info",
    "Join Interview",
    "Reschedule",
    "Cancel Interview",
    "No Show",
  ];
  const columns =
    userData && userData.is_admin
      ? [...baseColumns, "Delete Record"]
      : baseColumns;

  const rescheduleProblem = getRescheduleProblem();

  // only the first column is filterable, matching the trainings table
  const sortable = columns.map((_, idx) => idx === 0);
  const handleSorted = columns.map((_, idx) =>
    idx === 0 ? handleLanguageSort : false,
  );

  // return for the page
  return (
    <div>
      <NavBar />
      <Container className={styles.pageContainer}>
        <Container>
          <h1>Existing RA Interviews Page</h1>
        </Container>

        <Container className={styles.sectionContainer}>
          {isRefreshingCache && interviewCache.length === 0 && reauthorized && (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p style={{ marginTop: "15px", color: "#666" }}>
                Loading interviews...
              </p>
            </div>
          )}

          {loadError && (
            <div style={{ textAlign: "center", padding: "20px", color: "#a00" }}>
              {loadError}
            </div>
          )}

          {reauthorized && !isRefreshingCache && !loadError && (
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "15px",
                }}
              >
                <h4 style={{ margin: 0 }}>Scheduled RA Interviews</h4>
                <div>
                  <small style={{ marginRight: "10px", color: "#666" }}>
                    Last updated:{" "}
                    {lastRefreshTime
                      ? lastRefreshTime.toLocaleTimeString()
                      : "Loading..."}
                  </small>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => fetchAndCacheAllInterviews()}
                    disabled={isRefreshingCache}
                  >
                    {isRefreshingCache ? "Refreshing..." : "Refresh"}
                  </Button>
                </div>
              </div>

              <div
                className={
                  interviews.length === 0
                    ? styles.emptyTableMinHeight
                    : undefined
                }
              >
                <Table
                  columns={columns}
                  sortable={sortable}
                  handleSorted={handleSorted}
                  filters={[selectedLanguage]}
                  all={LANGUAGE_OPTIONS}
                  rows={
                    interviews.length > 0
                      ? interviews.map((record) => (
                          <InterviewRow record={record} key={record.id} />
                        ))
                      : [
                          <tr key="empty">
                            <td
                              colSpan={columns.length}
                              style={{ textAlign: "center", color: "#666" }}
                            >
                              {interviewCache.length === 0
                                ? "No RA interviews have been scheduled yet."
                                : `No ${selectedLanguage} RA interviews are scheduled.`}
                            </td>
                          </tr>,
                        ]
                  }
                />
              </div>

              {totalPage > 1 && (
                <MyPagination
                  total={totalPage}
                  current={curPage}
                  onChangePage={handleChangePage}
                />
              )}

              {isFetchingMore && (
                <div style={{ textAlign: "center", padding: "15px" }}>
                  <Spinner
                    animation="border"
                    size="sm"
                    role="status"
                    style={{ marginRight: "10px" }}
                  />
                  <span style={{ color: "#666" }}>
                    Loading more interviews...
                  </span>
                </div>
              )}

              {hasMoreInterviews && !isFetchingMore && (
                <div style={{ textAlign: "center", padding: "15px" }}>
                  <Button
                    variant="outline-primary"
                    onClick={() => loadMoreInterviews()}
                    disabled={isFetchingMore}
                  >
                    Load More Interviews
                  </Button>
                </div>
              )}
            </div>
          )}
        </Container>
      </Container>

      <Modal
        show={modalShow}
        onHide={() => setModalShow(false)}
        backdrop={undefined}
      >
        <Modal.Header closeButton={true}>
          <Modal.Title>{modalHeader}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalBody}</Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={deleteConfirmShow}
        onHide={() => {
          setDeleteConfirmShow(false);
          setDeleteInterviewData(null);
        }}
      >
        <Modal.Header closeButton={true}>
          <Modal.Title>Delete Interview Record</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteInterviewData && (
            <Container>
              <p>
                Permanently delete the interview record for{" "}
                <b>{deleteInterviewData.candidate_full_name}</b> on{" "}
                <b>
                  {deleteInterviewData.interview_date} at{" "}
                  {deleteInterviewData.interview_time}
                </b>
                ?
              </p>
              <p>
                Use this for past interviews, test entries and mistakes. The
                record is removed from this page for good.
              </p>
              <p style={{ color: "#a00" }}>
                Nobody is emailed.
                {!isCancelled(deleteInterviewData) &&
                  !isPast(deleteInterviewData) &&
                  " To let the candidate and interviewer know this interview is off, close this and use Cancel Interview instead."}
              </p>
            </Container>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setDeleteConfirmShow(false);
              setDeleteInterviewData(null);
            }}
            disabled={isDeletingInterview}
          >
            Keep record
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDelete}
            disabled={isDeletingInterview}
          >
            {isDeletingInterview ? "Deleting..." : "Delete record"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Cancel Confirmation Modal */}
      <Modal
        show={cancelConfirmShow}
        onHide={closeCancel}
        backdrop={isCancellingInterview ? "static" : true}
      >
        <Modal.Header closeButton={!isCancellingInterview}>
          <Modal.Title>Cancel Interview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {cancelInterviewData && (
            <Container>
              <p>
                Cancel the interview between{" "}
                <b>{cancelInterviewData.candidate_full_name}</b> and{" "}
                <b>{cancelInterviewData.interviewer_full_name}</b> on{" "}
                <b>
                  {cancelInterviewData.interview_date} at{" "}
                  {cancelInterviewData.interview_time}
                </b>
                ?
              </p>
              <p>
                This emails both of them that the interview is cancelled. The
                interview stays on this page, marked Cancelled.
                {cancelInterviewData.scheduler_email &&
                  ` ${cancelInterviewData.scheduler_email} is copied on the interviewer's email.`}
              </p>
              {cancelError && <p style={{ color: "#a00" }}>{cancelError}</p>}
            </Container>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeCancel}
            disabled={isCancellingInterview}
          >
            Keep interview
          </Button>
          <Button
            variant="warning"
            onClick={handleConfirmCancel}
            disabled={isCancellingInterview}
          >
            {isCancellingInterview
              ? "Sending emails..."
              : "Cancel interview and email both"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* No Show Confirmation Modal */}
      <Modal
        show={noShowConfirmShow}
        onHide={closeNoShow}
        backdrop={isMarkingNoShow ? "static" : true}
      >
        <Modal.Header closeButton={!isMarkingNoShow}>
          <Modal.Title>Mark as No Show</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {noShowInterviewData && (
            <Container>
              <p>
                Did <b>{noShowInterviewData.candidate_full_name}</b> miss their
                interview with{" "}
                <b>{noShowInterviewData.interviewer_full_name}</b> on{" "}
                <b>
                  {noShowInterviewData.interview_date} at{" "}
                  {noShowInterviewData.interview_time}
                </b>
                ?
              </p>
              <p>
                This emails both of them that the interview was missed. The
                interview stays on this page, marked No show, and rescheduling
                it later books a second attempt.
                {noShowInterviewData.scheduler_email &&
                  ` ${noShowInterviewData.scheduler_email} is copied on the interviewer's email.`}
              </p>
              {noShowError && <p style={{ color: "#a00" }}>{noShowError}</p>}
            </Container>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeNoShow}
            disabled={isMarkingNoShow}
          >
            They were there
          </Button>
          <Button
            variant="dark"
            onClick={handleConfirmNoShow}
            disabled={isMarkingNoShow}
          >
            {isMarkingNoShow
              ? "Sending emails..."
              : "Mark no show and email both"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reschedule Modal */}
      <Modal
        show={rescheduleShow}
        onHide={closeReschedule}
        backdrop={isReschedulingInterview ? "static" : true}
      >
        <Modal.Header closeButton={!isReschedulingInterview}>
          <Modal.Title>Reschedule Interview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {rescheduleInterviewData && (
            <Container>
              <p>
                <b>{rescheduleInterviewData.candidate_full_name}</b> with{" "}
                <b>{rescheduleInterviewData.interviewer_full_name}</b>
                <br />
                Currently {rescheduleInterviewData.interview_date} at{" "}
                {rescheduleInterviewData.interview_time} (CST)
              </p>
              <Form onSubmit={(e) => e.preventDefault()}>
                <Form.Group className="mb-3" controlId="rescheduleDate">
                  <Form.Label>New date</Form.Label>
                  <Form.Control
                    type="date"
                    value={newInterviewDate}
                    onChange={(e) => setNewInterviewDate(e.target.value)}
                    disabled={isReschedulingInterview}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="rescheduleTime">
                  <Form.Label>New time (CST)</Form.Label>
                  <Form.Control
                    type="time"
                    value={newInterviewTime}
                    onChange={(e) => setNewInterviewTime(e.target.value)}
                    disabled={isReschedulingInterview}
                    required
                  />
                </Form.Group>
              </Form>
              {rescheduleProblem && (
                <p
                  style={{
                    color:
                      rescheduleProblem === RESCHEDULE_UNCHANGED
                        ? "#666"
                        : "#a00",
                  }}
                >
                  {rescheduleProblem}
                </p>
              )}
              <p>
                This emails the candidate and the interviewer the new date and
                time, and tells them when it was previously scheduled. The
                interviewer stays the same.
                {rescheduleInterviewData.scheduler_email &&
                  ` ${rescheduleInterviewData.scheduler_email} is copied on the interviewer's email.`}
              </p>
              {rescheduleError && (
                <p style={{ color: "#a00" }}>{rescheduleError}</p>
              )}
            </Container>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeReschedule}
            disabled={isReschedulingInterview}
          >
            Keep current time
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmReschedule}
            disabled={isReschedulingInterview || Boolean(rescheduleProblem)}
          >
            {isReschedulingInterview
              ? "Sending emails..."
              : "Reschedule and email both"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ExistingInterviewsPage;
